import arrayLength from "@-/com.npmjs.extra-array__length";
/** Gap between keys horizontally and between rows vertically (matches `gap-1` / 4px). */
export const KEY_GAP = 4;

/** Horizontal inset on each side of the screen; keys and preview field are both inset by this amount. */
export const PINYIN_KEYBOARD_HORIZONTAL_GUTTER = 4;

export const PINYIN_KEYBOARD_ROW_LIST = [
  [
    { finalList: ["iu"], initial: "q", qwertyKey: "q" },
    { finalList: ["ei"], initial: "w", qwertyKey: "w" },
    { finalList: ["e"], qwertyKey: "e" },
    { finalList: ["uan"], initial: "r", qwertyKey: "r" },
    { finalList: ["üe", "ue"], initial: "t", qwertyKey: "t" },
    { finalList: ["un"], initial: "y", qwertyKey: "y" },
    { finalList: ["u"], initial: "sh", qwertyKey: "u" },
    { finalList: ["i"], initial: "ch", qwertyKey: "i" },
    { finalList: ["uo", "o"], qwertyKey: "o" },
    { finalList: ["ie"], initial: "p", qwertyKey: "p" },
    /** Neutral tone (tone 0); a single dot. Required to explicitly mark a neutral-tone answer. */
    { finalList: [], qwertyKey: "tone0" },
  ],
  [
    { finalList: ["a"], qwertyKey: "a" },
    { finalList: ["iong", "ong"], initial: "s", qwertyKey: "s" },
    { finalList: ["ai"], initial: "d", qwertyKey: "d" },
    { finalList: ["en"], initial: "f", qwertyKey: "f" },
    { finalList: ["eng"], initial: "g", qwertyKey: "g" },
    { finalList: ["ang"], initial: "h", qwertyKey: "h" },
    { finalList: ["an"], initial: "j", qwertyKey: "j" },
    { finalList: ["ing", "uai"], initial: "k", qwertyKey: "k" },
    { finalList: ["iang", "uang"], initial: "l", qwertyKey: "l" },
    /** First tone (flat); same action as former tone-1 key; label matches tone bar above `;`. */
    { finalList: ["\u02C9"], qwertyKey: ";" },
  ],
  [
    { finalList: ["ou"], initial: "z", qwertyKey: "z" },
    { finalList: ["ia", "ua"], initial: "x", qwertyKey: "x" },
    { finalList: ["ao"], initial: "c", qwertyKey: "c" },
    { finalList: ["ü", "ui"], initial: "zh", qwertyKey: "v" },
    { finalList: ["in"], initial: "b", qwertyKey: "b" },
    { finalList: ["iao"], initial: "n", qwertyKey: "n" },
    { finalList: ["ian"], initial: "m", qwertyKey: "m" },
  ],
] as const;

type PinyinKeyboardRow = (typeof PINYIN_KEYBOARD_ROW_LIST)[number];

export type PinyinKey = PinyinKeyboardRow[number];

export type PinyinQwertyKey = PinyinKey["qwertyKey"];

const DEFAULT_KEY_WIDTH = 56;

export function getPinyinKeyboardKeyWidth(windowWidth: number, showToneKeys: boolean) {
  const innerWidth = windowWidth - PINYIN_KEYBOARD_HORIZONTAL_GUTTER * 2;
  const g = KEY_GAP;
  if (innerWidth <= 0) {
    return DEFAULT_KEY_WIDTH;
  }
  const toneSlotCount = showToneKeys ? 3 : 0;
  const lastRowCells = arrayLength(PINYIN_KEYBOARD_ROW_LIST[2]) + toneSlotCount;
  const rowConstraintList = [
    { cells: arrayLength(PINYIN_KEYBOARD_ROW_LIST[0]) - (showToneKeys ? 0 : 1), rowIndex: 0 },
    { cells: arrayLength(PINYIN_KEYBOARD_ROW_LIST[1]) - (showToneKeys ? 0 : 1), rowIndex: 1 },
    { cells: lastRowCells, rowIndex: 2 },
  ] as const;
  let minKeyWidth = Infinity;
  for (const { cells, rowIndex } of rowConstraintList) {
    const coeff = rowIndex / 2 + cells;
    const rhs = innerWidth - g * (rowIndex / 2 + cells - 1);
    if (coeff <= 0 || rhs <= 0) {
      continue;
    }
    minKeyWidth = Math.min(minKeyWidth, rhs / coeff);
  }
  return Number.isFinite(minKeyWidth) ? Math.max(28, minKeyWidth) : DEFAULT_KEY_WIDTH;
}

/**
 * Locks keys to a portrait shape regardless of available width. >1 = portrait
 * (taller than wide, looks like a phone keyboard); <1 = landscape (laptop-style).
 * Phone keyboards are noticeably taller than wide, so we match that on wide screens
 * (web) instead of letting the keys go square. The 42px floor is a tap-target
 * safety net for very narrow screens.
 */
const KEY_ASPECT_RATIO_HEIGHT_OVER_WIDTH = 1.2;

export function getPinyinKeyboardKeyHeight(keyWidth: number) {
  return Math.max(42, Math.floor(keyWidth * KEY_ASPECT_RATIO_HEIGHT_OVER_WIDTH));
}

/**
 * Absolute inset for the toggle on the bottom row, aligned with the top-row `Q` key (no row
 * stagger). Horizontal edge inset comes from the host wrapper (e.g. `AboveKeyboard`'s `px-1`), so
 * the keys grid and toggle both start at the container's left edge.
 */
export function getFloatingPinyinKeyboardToggleKeyboardSlotInset() {
  return {
    bottom: 0,
    left: 0,
  };
}

/** Absolute inset when the keyboard is hidden and the toggle floats in the footer column. */
export function getFloatingPinyinKeyboardToggleFooterInset() {
  return {
    bottom: 0,
    left: 0,
  };
}
